import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import type { Preferences, PreferencesRequest } from '@/types';

export function usePreferences() {
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.getPreferences();
      setPreferences(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const savePreferences = async (data: PreferencesRequest) => {
    try {
      const updated = await api.savePreferences(data);
      setPreferences(updated);
      return updated;
    } catch (err) {
      throw err;
    }
  };

  /**
   * @deprecated Use DeliveryScheduleManager component instead.
   * This updates the old single-schedule system. Use the delivery_schedules API for multiple schedules.
   */
  const updateDeliveryTime = async (time: string, timezone?: string) => {
    await api.updateDeliveryTime(time, timezone);
    await fetchPreferences();
  };

  const toggleStatus = async (isActive: boolean) => {
    await api.toggleNewsletterStatus(isActive);
    await fetchPreferences();
  };

  return {
    preferences,
    isLoading,
    error,
    savePreferences,
    updateDeliveryTime,
    toggleStatus,
    refresh: fetchPreferences,
  };
}

